import React, {
  useState,
  useMemo,
  useEffect,
  startTransition,
  useCallback,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BrandProvider } from "./contexts/BrandContext";
import { PWAProvider } from "./contexts/PWAContext";
import ErrorBoundary from "./components/ErrorBoundary";
import GlobalErrorHandler from "./utils/errorHandler";
import { SEOHead } from "./components/Common/SEOHead";
import {
  memberApi,
  townApi,
  contributionApi,
  eventApi,
  expenseApi,
  benefitApi,
} from "./services/api";
import { setDefaultFavicon } from "./utils/favicon";
import { ToastContainer } from "./components/Common/NotificationToast";
import { ToastProvider, useGlobalToast } from "./contexts/ToastContext";
import { MemberToastProvider } from "./contexts/MemberToastContext";
import { HelpProvider } from "./contexts/HelpContext";
import { HelpModal } from "./components/Help/HelpModal";
import { NewGuideNotifications } from "./components/Help/NewGuideNotification";
import { useConfirmation } from "./hooks/useConfirmation";
import { ConfirmationModal } from "./components/Common/ConfirmationModal";
import PWAInstallPrompt from "./components/Common/PWAInstallPrompt";
import MobileNavigation from "./components/Layout/MobileNavigation";
import { LandingPage } from "./components/Landing/LandingPage";
import { DeveloperLoginPage } from "./components/Auth/DeveloperLoginPage";
import { LoginModal } from "./components/Auth/LoginModal";
import { Header } from "./components/Layout/Header";
import { Sidebar } from "./components/Layout/Sidebar";
// Lazy load major components
const Dashboard = React.lazy(() =>
  import("./components/Dashboard/Dashboard").then((m) => ({
    default: m.Dashboard,
  })),
);
const MemberList = React.lazy(() =>
  import("./components/Members/MemberList").then((m) => ({
    default: m.MemberList,
  })),
);
const TownList = React.lazy(() =>
  import("./components/Towns/TownList").then((m) => ({ default: m.TownList })),
);
const ContributionList = React.lazy(() =>
  import("./components/Contributions/ContributionList").then((m) => ({
    default: m.ContributionList,
  })),
);
const EventList = React.lazy(() =>
  import("./components/Events/EventList").then((m) => ({
    default: m.EventList,
  })),
);
const ExpenseList = React.lazy(() =>
  import("./components/Expenses/ExpenseList").then((m) => ({
    default: m.ExpenseList,
  })),
);
const BenefitList = React.lazy(() =>
  import("./components/Benefits/BenefitList").then((m) => ({
    default: m.BenefitList,
  })),
);
const SMSCenter = React.lazy(() =>
  import("./components/SMS/SMSCenter").then((m) => ({ default: m.SMSCenter })),
);
const EmailCenter = React.lazy(() =>
  import("./components/Email/EmailCenter").then((m) => ({
    default: m.EmailCenter,
  })),
);
const MessagingPage = React.lazy(() =>
  import("./components/Messages/MessagingPage").then((m) => ({
    default: m.MessagingPage,
  })),
);
const ReportCenter = React.lazy(() =>
  import("./components/Reports/ReportCenter").then((m) => ({
    default: m.ReportCenter,
  })),
);
const Settings = React.lazy(() =>
  import("./components/Settings/Settings").then((m) => ({
    default: m.Settings,
  })),
);
const BrandSettings = React.lazy(
  () => import("./components/Admin/BrandSettings"),
);
const ReminderManager = React.lazy(
  () => import("./components/Reminders/ReminderManager"),
);
const AuditViewer = React.lazy(() =>
  import("./components/Audit/AuditViewer").then((m) => ({
    default: m.AuditViewer,
  })),
);
const FinancialApprovalQueue = React.lazy(() =>
  import("./components/Approvals/FinancialApprovalQueue").then((m) => ({
    default: m.FinancialApprovalQueue,
  })),
);
const PaymentSettings = React.lazy(() =>
  import("./components/Admin/PaymentSettings").then((m) => ({
    default: m.PaymentSettings,
  })),
);
const EnhancedMonthlyAssignmentDashboard = React.lazy(() =>
  import("./components/Assignments/EnhancedMonthlyAssignmentDashboard").then(
    (m) => ({ default: m.EnhancedMonthlyAssignmentDashboard }),
  ),
);
const MemberPortal = React.lazy(() =>
  import("./components/MemberPortal/MemberPortal").then((m) => ({
    default: m.MemberPortal,
  })),
);
const DeveloperPortal = React.lazy(() =>
  import("./components/Developer/DeveloperPortal").then((m) => ({
    default: m.DeveloperPortal,
  })),
);
const StatsDashboard = React.lazy(() =>
  import("./components/Stats/StatsDashboard").then((m) => ({
    default: m.StatsDashboard,
  })),
);
const MaintenancePage = React.lazy(() =>
  import("./components/Common/MaintenancePage").then((m) => ({
    default: m.default,
  })),
);

// Keep modals and small components as regular imports
import { RoleBasedRoute } from "./components/Common/RoleBasedRoute";
import { PERMISSIONS } from "./utils/permissions";
import { MemberDetailModal } from "./components/Members/MemberDetailModal";
import { EditMemberModal } from "./components/Members/EditMemberModal";
import { EventAttendanceModal } from "./components/Events/EventAttendanceModal";
import { MoMoPaymentModal } from "./components/Payments/MoMoPaymentModal";
import { AddTownModal } from "./components/Towns/AddTownModal";
import { EditTownModal } from "./components/Towns/EditTownModal";
import { TownDetailModal } from "./components/Towns/TownDetailModal";
import { AddExpenseModal } from "./components/Expenses/AddExpenseModal";
// Removed mock data imports - using API data only
import {
  DashboardStats,
  Member,
  Town,
  Contribution,
  Event,
  Expense,
  Benefit,
  User,
} from "./types";
import {
  exportMembersReport,
  exportFinancialReport,
  exportEventReport,
  exportExpenseReport,
} from "./utils/excel";
import { buildApiUrl } from "./config/api";

const AppContent: React.FC = () => {
  const { user } = useAuth();
  const { toasts, removeToast, success, error } = useGlobalToast();
  const { isOpen, options, confirm, handleConfirm, handleCancel } =
    useConfirmation();

  // All hooks must be called consistently at the top level
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginRole, setLoginRole] = useState<"admin" | "member">("admin");
  const [towns, setTowns] = useState<Town[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedTownId, setSelectedTownId] = useState("all");
  const [activeTab, setActiveTab] = useState("dashboard");

  const handleTabChange = useCallback((tab: string) => {
    startTransition(() => {
      setActiveTab(tab);
    });
  }, []);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [selectedTown, setSelectedTown] = useState<Town | null>(null);
  const [memberDetailModalOpen, setMemberDetailModalOpen] = useState(false);
  const [editMemberModalOpen, setEditMemberModalOpen] = useState(false);
  const [addTownModalOpen, setAddTownModalOpen] = useState(false);
  const [editTownModalOpen, setEditTownModalOpen] = useState(false);
  const [townDetailModalOpen, setTownDetailModalOpen] = useState(false);
  const [addExpenseModalOpen, setAddExpenseModalOpen] = useState(false);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [momoPaymentModalOpen, setMomoPaymentModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState<{
    amount: number;
    type: string;
    memberId: string;
    townId: string;
    memberName: string;
  } | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    window.innerWidth < 1024,
  );
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);
  const [isRefreshingDashboard, setIsRefreshingDashboard] = useState(false);
  const [isRefreshingMembers, setIsRefreshingMembers] = useState(false);
  const [isRefreshingReports, setIsRefreshingReports] = useState(false);
  const [isRefreshingEvents, setIsRefreshingEvents] = useState(false);

  // Helper function to ensure Benefit objects have all required properties
  const normalizeBenefit = (benefit: Partial<Benefit>): Benefit => {
    return {
      ...benefit,
      createdAt: benefit.createdAt || new Date().toISOString(),
    } as Benefit;
  };

  // Check maintenance mode status
  useEffect(() => {
    const checkMaintenanceStatus = async () => {
      try {
        const response = await fetch(buildApiUrl("/api/maintenance-status"));
        const data = await response.json();

        // Type guard for maintenance status response
        if (
          typeof data === "object" &&
          data !== null &&
          "success" in data &&
          typeof data.success === "boolean" &&
          "maintenance" in data &&
          typeof data.maintenance === "boolean"
        ) {
          setIsMaintenanceMode(data.maintenance);
        } else {
          setIsMaintenanceMode(false);
        }
      } catch (error) {
        console.error("Failed to check maintenance status:", error);
        // Default to false if check fails
        setIsMaintenanceMode(false);
      } finally {
        setMaintenanceChecked(true);
      }
    };

    checkMaintenanceStatus();
  }, []);

  // Calculate dashboard statistics based on selected town
  const dashboardStats: DashboardStats = useMemo(() => {
    const filteredMembers =
      selectedTownId === "all"
        ? members
        : selectedTownId === "no-town-assigned"
          ? []
          : members.filter((m) => m.townId === selectedTownId);

    const filteredContributions =
      selectedTownId === "all"
        ? contributions
        : selectedTownId === "no-town-assigned"
          ? []
          : contributions.filter((c) => c.townId === selectedTownId);

    const totalRevenue = filteredContributions
      .filter(
        (c) =>
          c.status === "Completed" &&
          c.approvalStatus === "approved",
      )
      .reduce((sum, c) => sum + c.amount, 0);

    const filteredExpenses =
      selectedTownId === "all"
        ? expenses
        : selectedTownId === "no-town-assigned"
          ? []
          : expenses.filter((e) => e.townId === selectedTownId);

    const totalExpenses = filteredExpenses
      .filter((e) => e.approvalStatus === "approved")
      .reduce((sum, e) => sum + e.amount, 0);

    const filteredBenefits =
      selectedTownId === "all"
        ? benefits
        : selectedTownId === "no-town-assigned"
          ? []
          : benefits.filter((b) => b.townId === selectedTownId);

    const totalPaidBenefits = filteredBenefits
      .filter((b) => b.approvalStatus === "approved")
      .reduce((sum, b) => sum + b.amount, 0);

    // Calculate growth metrics
    const currentDate = new Date();
    const thirtyDaysAgo = new Date(
      currentDate.getTime() - 30 * 24 * 60 * 60 * 1000,
    );

    // Member growth
    const recentMembers = filteredMembers.filter(
      (m) => new Date(m.registrationDate) >= thirtyDaysAgo,
    ).length;
    const oldMembers = filteredMembers.length - recentMembers;
    const memberGrowth =
      oldMembers > 0 ? (recentMembers / oldMembers) * 100 : 0;

    // Revenue growth
    const recentRevenue = filteredContributions
      .filter(
        (c) =>
          new Date(c.date) >= thirtyDaysAgo &&
          c.status === "Completed" &&
          c.approvalStatus === "approved",
      )
      .reduce((sum, c) => sum + c.amount, 0);
    const revenueGrowth =
      totalRevenue > 0 ? (recentRevenue / totalRevenue) * 100 : 0;

    // Balance change
    const recentExpenses = filteredExpenses
      .filter(
        (e) =>
          new Date(e.date) >= thirtyDaysAgo && e.approvalStatus === "approved",
      )
      .reduce((sum, e) => sum + e.amount, 0);
    const recentBenefits = filteredBenefits
      .filter(
        (b) => new Date(b.requestDate) >= thirtyDaysAgo && b.approvalStatus === "approved",
      )
      .reduce((sum, b) => sum + b.amount, 0);
    const netRecentFlow = recentRevenue - recentExpenses - recentBenefits;
    const currentBalance = totalRevenue - totalExpenses - totalPaidBenefits;
    const balanceChange =
      currentBalance !== 0
        ? (netRecentFlow / Math.abs(currentBalance)) * 100
        : 0;

    return {
      totalMembers: filteredMembers.length,
      memberGrowth,
      totalTowns: towns.length,
      townGrowth: 10,
      totalRevenue,
      revenueGrowth,
      currentBalance,
      balanceChange,
      totalCollections: totalRevenue,
      pendingApprovals:
        filteredContributions.filter(
          (c) =>
            c.approvalStatus === "pending"
        ).length +
        filteredExpenses.filter((e) => e.approvalStatus === "pending").length +
        filteredBenefits.filter((b) => b.approvalStatus === "pending").length,
    };
  }, [selectedTownId, members, contributions, expenses, benefits, towns]);

  // Initialize global error handler and default favicon
  useEffect(() => {
    GlobalErrorHandler.init();
    setDefaultFavicon();
  }, []);

  // Update selectedTownId when user changes
  useEffect(() => {
    if (user) {
      if (user.role === "member") {
        setSelectedTownId(user.townId || "1");
      } else if (
        (user.role === "town_admin" || user.role === "member_admin") &&
        !user.townId
      ) {
        setSelectedTownId("no-town-assigned");
      } else if (
        (user.role === "town_admin" || user.role === "member_admin") &&
        user.townId
      ) {
        setSelectedTownId(user.townId);
      } else {
        setSelectedTownId("all");
      }
    }
  }, [user]);

  const fetchAllData = useCallback(async () => {
    try {
      // Base API calls that all users can access
      const basePromises = [
        memberApi.getAll(),
        townApi.getAll(),
        contributionApi.getAll(),
        eventApi.getAll(),
      ];

      // Admin-only API calls
      const adminPromises = [];
      if (
        user &&
        ["admin", "finance_admin", "town_admin", "developer"].includes(
          user.role,
        )
      ) {
        adminPromises.push(
          expenseApi.getAll(),
          benefitApi.getAll(),
          fetch(buildApiUrl("/api/admins"), {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            },
          }),
        );
      }

      // Wait for all promises to resolve
      const responses = await Promise.all([...basePromises, ...adminPromises]);

      // Helper function to check if response is ApiResponse
      const isApiResponse = (
        response: unknown,
      ): response is { success: boolean; data?: unknown; error?: string } => {
        return (
          typeof response === "object" &&
          response !== null &&
          "success" in response
        );
      };

      // Helper function to check if data is of specific type
      const isMemberArray = (data: unknown): data is Member[] => {
        return (
          Array.isArray(data) &&
          data.every(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              "id" in item &&
              "name" in item &&
              "username" in item,
          )
        );
      };

      const isTownArray = (data: unknown): data is Town[] => {
        return (
          Array.isArray(data) &&
          data.every(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              "id" in item &&
              "name" in item,
          )
        );
      };

      const isContributionArray = (data: unknown): data is Contribution[] => {
        return (
          Array.isArray(data) &&
          data.every(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              "id" in item &&
              "memberId" in item &&
              "amount" in item,
          )
        );
      };

      const isEventArray = (data: unknown): data is Event[] => {
        return (
          Array.isArray(data) &&
          data.every(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              "id" in item &&
              "title" in item &&
              "date" in item,
          )
        );
      };

      const isExpenseArray = (data: unknown): data is Expense[] => {
        return (
          Array.isArray(data) &&
          data.every(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              "id" in item &&
              "amount" in item &&
              "category" in item,
          )
        );
      };

      const isBenefitArray = (data: unknown): data is Benefit[] => {
        return (
          Array.isArray(data) &&
          data.every(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              "id" in item &&
              "memberId" in item &&
              "amount" in item &&
              "type" in item,
          )
        );
      };

      const isUserArray = (data: unknown): data is User[] => {
        return (
          Array.isArray(data) &&
          data.every(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              "id" in item &&
              "username" in item &&
              "role" in item,
          )
        );
      };

      // Handle base responses (always present)
      const membersRes = responses[0];
      const townsRes = responses[1];
      const contributionsRes = responses[2];
      const eventsRes = responses[3];

      // Ensure data is properly resolved before setting state with type guards
      const resolvedMembers =
        isApiResponse(membersRes) &&
        membersRes.success &&
        isMemberArray(membersRes.data)
          ? membersRes.data
          : [];
      const resolvedTowns =
        isApiResponse(townsRes) &&
        townsRes.success &&
        isTownArray(townsRes.data)
          ? townsRes.data
          : [];
      const resolvedContributions =
        isApiResponse(contributionsRes) &&
        contributionsRes.success &&
        isContributionArray(contributionsRes.data)
          ? contributionsRes.data
          : [];
      const resolvedEvents =
        isApiResponse(eventsRes) &&
        eventsRes.success &&
        isEventArray(eventsRes.data)
          ? eventsRes.data
          : [];

      // Set state with resolved data
      setMembers(resolvedMembers);
      setTowns(resolvedTowns);
      setContributions(resolvedContributions);
      setEvents(resolvedEvents);

      console.log("Setting members in App.tsx:", resolvedMembers.length);

      // Handle admin-only results
      if (responses.length > 4) {
        const expensesRes = responses[4];
        const benefitsRes = responses[5];
        const usersRes = responses[6];

        const resolvedExpenses =
          isApiResponse(expensesRes) &&
          expensesRes.success &&
          isExpenseArray(expensesRes.data)
            ? expensesRes.data
            : [];
        const resolvedBenefits =
          isApiResponse(benefitsRes) &&
          benefitsRes.success &&
          isBenefitArray(benefitsRes.data)
            ? benefitsRes.data.map(normalizeBenefit)
            : [];

        setExpenses(resolvedExpenses);
        setBenefits(resolvedBenefits);

        if (
          usersRes &&
          typeof usersRes === "object" &&
          "ok" in usersRes &&
          usersRes.ok
        ) {
          const usersData = await usersRes.json();
          const resolvedUsers =
            isApiResponse(usersData) &&
            usersData.success &&
            isUserArray(usersData.data)
              ? usersData.data
              : [];
          setUsers(resolvedUsers);
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      // Set empty arrays to prevent Promise rendering
      setMembers([]);
      setTowns([]);
      setContributions([]);
      setEvents([]);
      setExpenses([]);
      setBenefits([]);
      setUsers([]);
    } finally {
      setDataLoaded(true);
    }
  }, [user]);

  // Fetch data from API when user is authenticated (skip for pure developer role)
  useEffect(() => {
    if (
      user &&
      !dataLoaded &&
      !(user.role === "developer" && !user.isDevAdminView)
    ) {
      // Add a small delay to prevent blocking the UI
      const timer = setTimeout(() => {
        fetchAllData();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, dataLoaded, user?.role, user?.isDevAdminView, fetchAllData]);

  // Debug: Check for Promise rendering issues
  useEffect(() => {
    const checkForPromises = (obj: unknown, path: string = ""): void => {
      if (obj && typeof obj === "object") {
        for (const [key, value] of Object.entries(obj)) {
          const currentPath = path ? `${path}.${key}` : key;
          if (value instanceof Promise) {
            console.warn(`[DEBUG] Promise found at ${currentPath}:`, value);
          } else if (typeof value === "object" && value !== null) {
            checkForPromises(value, currentPath);
          }
        }
      }
    };

    // Check data objects for Promises
    checkForPromises(members, "members");
    checkForPromises(contributions, "contributions");
    checkForPromises(events, "events");
    checkForPromises(expenses, "expenses");
    checkForPromises(benefits, "benefits");
    checkForPromises(towns, "towns");
    checkForPromises(user, "user");
  }, [members, contributions, events, expenses, benefits, towns, user]);

  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarCollapsed(false);
      } else {
        setSidebarCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle navigation from notifications
  useEffect(() => {
    const handleNavigateToTab = (event: CustomEvent) => {
      const { tab } = event.detail;
      // Map notification tabs to actual app tabs
      const tabMapping: Record<string, string> = {
        payments: "contributions",
        events: "events",
        members: "members",
        dashboard: "dashboard",
      };
      const actualTab = tabMapping[tab] || tab;
      handleTabChange(actualTab);
    };

    window.addEventListener(
      "navigate-to-tab",
      handleNavigateToTab as EventListener,
    );
    return () =>
      window.removeEventListener(
        "navigate-to-tab",
        handleNavigateToTab as EventListener,
      );
  }, [handleTabChange]);

  // Handle mobile quick actions
  const handleQuickAction = useCallback(
    (action: string): void => {
      let targetTab = "dashboard";
      switch (action) {
        case "add-member":
        case "members":
          targetTab = "members";
          break;
        case "record-payment":
        case "contributions":
          targetTab = "contributions";
          break;
        case "create-event":
        case "events":
          targetTab = "events";
          break;
        case "send-sms":
        case "sms":
          targetTab = "sms";
          break;
        case "payment-settings":
          targetTab = "payment-settings";
          break;
        case "approvals":
          targetTab = "approvals";
          break;
        case "generate-report":
        case "reports":
          targetTab = "member-reports";
          break;
        default:
          return;
      }
      handleTabChange(targetTab);
    },
    [handleTabChange],
  );

  useEffect(() => {
    const handleMobileQuickAction = (event: CustomEvent) => {
      const { action } = event.detail;
      handleQuickAction(action);
    };

    window.addEventListener(
      "mobile-quick-action",
      handleMobileQuickAction as EventListener,
    );
    return () =>
      window.removeEventListener(
        "mobile-quick-action",
        handleMobileQuickAction as EventListener,
      );
  }, [handleQuickAction]);

  // Handle portal selection (only admin and member now)
  const handlePortalSelection = (portal: "admin" | "member") => {
    setLoginRole(portal);
    setLoginModalOpen(true);
  };

  // Handle admin switching to member portal
  const handleSwitchToMemberPortal = (townId: string) => {
    // Force a data refresh and re-render
    setDataLoaded(false);
    setSelectedTownId(townId);

    // Simple page reload to ensure proper context switch
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleSimulateMember = async (member: Member) => {
    try {
      const currentToken = localStorage.getItem("auth_token");
      if (!currentToken) return;

      // Use member's town ID, or fallback to '1' if not set
      const townId = member.townId || "1";

      const response = await authApi.switchToMemberPortal(
        currentToken,
        townId,
        member.id,
      );

      if (response.success && response.data) {
        localStorage.setItem("original_auth_token", currentToken);
        localStorage.setItem("auth_token", String(response.data.token));
        localStorage.setItem("user_data", JSON.stringify(response.data.user));

        // Reload to switch context
        window.location.reload();
      } else {
        error(
          "Simulation Failed",
          response.error || "Failed to simulate member.",
        );
      }
    } catch (err) {
      console.error("Simulation error:", err);
      // Only show error if it's not a page reload interruption
      if (err instanceof Error && err.name !== "AbortError") {
        error("Simulation Failed", "Network error occurred.");
      }
    }
  };

  const handleRefreshDashboard = async () => {
    setIsRefreshingDashboard(true);
    try {
      await fetchAllData();
    } finally {
      setIsRefreshingDashboard(false);
    }
  };

  const handleRefreshMembers = async () => {
    setIsRefreshingMembers(true);
    try {
      await fetchAllData();
    } finally {
      setIsRefreshingMembers(false);
    }
  };

  const handleRefreshReports = async () => {
    setIsRefreshingReports(true);
    try {
      await fetchAllData();
    } finally {
      setIsRefreshingReports(false);
    }
  };

  const handleRefreshEvents = async () => {
    setIsRefreshingEvents(true);
    try {
      await fetchAllData();
    } finally {
      setIsRefreshingEvents(false);
    }
  };

  // Return routing structure for unauthenticated users
  if (!user) {
    return (
      <Routes>
        <Route
          path="/"
          element={
            <>
              <SEOHead
                title="Family Membership Portal"
                description="Access your Ghana family membership portal with secure login for admins and members."
              />
              <LandingPage onSelectPortal={handlePortalSelection} />
              <LoginModal
                isOpen={loginModalOpen}
                onClose={() => {
                  setLoginModalOpen(false);
                }}
                role={loginRole}
              />
            </>
          }
        />
        <Route path="/nexus" element={<DeveloperLoginPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Show developer portal for developers (but not when they've switched to admin roles)
  if (user.role === "developer" && !user.isDevAdminView) {
    return (
      <React.Suspense
        fallback={
          <div className="loading-container">
            <div className="text-center">
              <div className="loading-spinner"></div>
              <p className="mt-4 text-gray-600">Loading Developer Portal...</p>
            </div>
          </div>
        }
      >
        <DeveloperPortal />
      </React.Suspense>
    );
  }

  // Show maintenance page if maintenance mode is active (for non-developers)
  if (maintenanceChecked && isMaintenanceMode && user.role !== "developer") {
    return (
      <React.Suspense
        fallback={
          <div className="loading-container">
            <div className="text-center">
              <div className="loading-spinner"></div>
              <p className="mt-4 text-gray-600">Loading Maintenance Page...</p>
            </div>
          </div>
        }
      >
        <MaintenancePage />
      </React.Suspense>
    );
  }

  // Show member portal for members or admin member view
  if (
    user.role === "member" ||
    user.isAdminMemberView ||
    user.role === "member_portal_admin"
  ) {
    // Don't render until data is loaded
    if (!dataLoaded || towns.length === 0) {
      return (
        <div className="loading-container">
          <div className="text-center">
            <div className="loading-spinner"></div>
            <p className="mt-4 text-gray-600">Loading Member Portal...</p>
          </div>
        </div>
      );
    }

    const memberTown = towns.find((t) => t.id === user.townId) ||
      towns[0] || {
        id: "1",
        name: "Default Town",
        location: "Ghana",
        memberCount: 0,
        contactPerson: "Admin",
      };

    // For admin member view, create a representative member data with proper member ID structure
    const memberData = user.isAdminMemberView
      ? {
          id: user.originalUserId || user.id, // Use original member ID if available
          username: user.username,
          name: user.name || "Admin Member View",
          phone: "0XX XXX XXXX",
          gender: "Male" as const,
          ageGroup: "Adult" as const,
          townId: user.townId || memberTown.id,
          registrationDate: new Date().toISOString().split("T")[0],
          totalContributions: 0,
          lastPaymentDate: undefined,
          createdAt: new Date().toISOString(),
          isAdminView: true,
          originalAdminId: user.id, // Store admin ID for reference
          originalAdminRole: user.role,
        }
      : members.find(
          (m) => m.id === user.id || m.username === user.username,
        ) || {
          id: user.id,
          username: user.username,
          name: user.name || "Member",
          phone: user.phone || "",
          gender: "Male" as const,
          ageGroup: "Adult" as const,
          townId: user.townId || memberTown.id,
          registrationDate: "2024-01-01",
          totalContributions: 450.0,
          lastPaymentDate: "2024-01-15",
          createdAt: "2024-01-01T00:00:00.000Z",
        };

    const townEvents = events.filter((e) => e.townId === user.townId);
    const memberContributions = user.isAdminMemberView
      ? []
      : contributions.filter(
          (c) => c.memberId === user.id || c.memberId === user.username,
        );

    return (
      <>
        <SEOHead
          title={
            user.isAdminMemberView
              ? "Admin Member Portal View"
              : "Member Portal"
          }
          description="Access your family membership portal to view contributions, make payments, and stay updated on events."
          canonical={window.location.href}
        />
        <React.Suspense
          fallback={
            <div className="loading-container">
              <div className="text-center">
                <div className="loading-spinner"></div>
                <p className="mt-4 text-gray-600">Loading Member Portal...</p>
              </div>
            </div>
          }
        >
          <MemberToastProvider>
            <MemberPortal
              member={memberData}
              town={memberTown}
              contributions={memberContributions}
              events={townEvents}
            />
          </MemberToastProvider>
        </React.Suspense>
      </>
    );
  }

  const handleNavigateToMessages = (contactId?: string) => {
    handleTabChange("messages");
    // Store contact ID for the messaging page to use
    if (contactId) {
      sessionStorage.setItem("selectedContactId", contactId);
    }
  };

  const handleExportMembers = () => {
    exportMembersReport(members, towns, selectedTownId, {
      contributions,
      events,
      benefits,
      expenses,
    });
  };

  const handleExportFinancial = () => {
    // Ensure we're passing the correct types
    const validContributions: Contribution[] = contributions.filter(
      (c): c is Contribution =>
        !!c.id && !!c.memberId && typeof c.amount === "number",
    );
    const validExpenses: Expense[] = expenses.filter(
      (e): e is Expense =>
        !!e.id && typeof e.amount === "number" && !!e.category,
    );
    exportFinancialReport(
      validContributions,
      validExpenses,
      towns,
      members,
      selectedTownId,
    );
  };

  const handleViewMember = (member: Member) => {
    setSelectedMember(member);
    setMemberDetailModalOpen(true);
  };

  const handleEditMember = (member: Member) => {
    setSelectedMember(member);
    setEditMemberModalOpen(true);
  };

  const handleSaveMember = async (updatedMember: Member) => {
    try {
      const response = await memberApi.update(updatedMember.id, updatedMember);
      if (response.success) {
        setMembers((prev) =>
          prev.map((m) => (m.id === updatedMember.id ? updatedMember : m)),
        );
        success(
          "Member Updated",
          "Member information has been updated successfully.",
        );
      } else {
        error("Update Failed", response.error || "Failed to update member.");
      }
    } catch (err) {
      console.error("Failed to update member:", err);
      error("Update Failed", "An error occurred while updating the member.");
    }
  };

  const handleAddMember = async (
    memberData: Omit<Member, "id" | "createdAt">,
  ) => {
    try {
      console.log("Creating member with data:", memberData);
      const response = await memberApi.create(memberData);
      console.log("Member creation response:", response);

      if (response.success && response.data) {
        setMembers((prev) => [...prev, response.data!]);
        success(
          "Member Added",
          `${memberData.name} has been added successfully.`,
        );

        // Refresh data to ensure consistency
        setTimeout(() => {
          fetchAllData();
        }, 1000);
      } else {
        error(
          "Failed to Add Member",
          response.error || "An error occurred while adding the member.",
        );
      }
    } catch (err) {
      console.error("Failed to add member:", err);
      error(
        "Failed to Add Member",
        "Network error occurred while adding the member.",
      );
    }
  };

  const handleAddContribution = async (
    contributionData: Omit<Contribution, "id" | "createdAt">,
  ) => {
    try {
      const response = await contributionApi.create(contributionData);
      if (response.success && response.data) {
        setContributions((prev) => [...prev, response.data!]);

        // Update member's total contributions
        setMembers((prev) =>
          prev.map((m) =>
            m.id === contributionData.memberId
              ? {
                  ...m,
                  totalContributions:
                    m.totalContributions + contributionData.amount,
                  lastPaymentDate: contributionData.date,
                }
              : m,
          ),
        );

        const memberName =
          members.find((m) => m.id === contributionData.memberId)?.name ||
          "Member";
        success(
          "Contribution Added",
          `${memberName}'s contribution of GHS ${contributionData.amount.toFixed(2)} has been recorded successfully.`,
        );

        // Refresh the table to show the new contribution
        setTimeout(() => {
          fetchAllData();
        }, 500);
      } else {
        error(
          "Failed to Add Contribution",
          response.error || "An error occurred while adding the contribution.",
        );
      }
    } catch (err) {
      console.error("Failed to add contribution:", err);
      error(
        "Failed to Add Contribution",
        "Network error occurred while adding the contribution.",
      );
    }
  };

  const handleAddEvent = async (eventData: Omit<Event, "id" | "createdAt">) => {
    try {
      const response = await eventApi.create(eventData);
      if (response.success && response.data) {
        setEvents((prev) => [...prev, response.data!]);
      }
    } catch (error) {
      console.error("Failed to add event:", error);
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    const confirmed = await confirm({
      title: "Delete Member",
      message:
        "Are you sure you want to delete this member? This action cannot be undone.",
      confirmText: "Delete",
      type: "danger",
    });

    if (confirmed) {
      try {
        const response = await memberApi.delete(memberId);
        if (response.success) {
          setMembers((prev) => prev.filter((m) => m.id !== memberId));
          setContributions((prev) =>
            prev.filter((c) => c.memberId !== memberId),
          );
          success("Member Deleted", "Member has been deleted successfully.");
        } else {
          error("Delete Failed", response.error || "Failed to delete member.");
        }
      } catch (err) {
        console.error("Failed to delete member:", err);
        error("Delete Failed", "An error occurred while deleting the member.");
      }
    }
  };

  const handleViewEvent = (event: Event) => {
    setSelectedEvent(event);
    setAttendanceModalOpen(true);
  };

  const handleSaveAttendance = async (
    eventId: string,
    attendance: Record<string, "present" | "absent">,
  ) => {
    try {
      // Convert record object to array of EventAttendance
      const attendanceArray = Object.entries(attendance).map(
        ([memberId, status]) => ({
          memberId,
          status,
          date: new Date().toISOString(),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }),
      ) as any[];

      const response = await eventApi.updateAttendance(
        eventId,
        attendanceArray,
      );

      if (response.success) {
        success(
          "Attendance Saved",
          "Event attendance has been updated successfully.",
        );
        // Refresh events to show updated stats
        handleRefreshEvents();
        setAttendanceModalOpen(false);
      } else {
        error("Save Failed", response.error || "Failed to update attendance.");
      }
    } catch (err) {
      console.error("Failed to save attendance:", err);
      error("Save Failed", "Network error occurred while saving attendance.");
    }
  };

  const handleAddExpense = () => {
    setAddExpenseModalOpen(true);
  };

  const handleSaveTown = async (
    townData: Omit<
      Town,
      "id" | "memberCount" | "totalRevenue" | "totalExpenses" | "currentBalance"
    >,
  ) => {
    try {
      const response = await townApi.create(townData);
      if (response.success && response.data) {
        setTowns((prev) => [...prev, response.data!]);
        success("Town Added", `${townData.name} has been added successfully.`);
      } else {
        error(
          "Failed to Add Town",
          response.error || "An error occurred while adding the town.",
        );
      }
    } catch (err) {
      console.error("Failed to add town:", err);
      error(
        "Failed to Add Town",
        "Network error occurred while adding the town.",
      );
    }
  };

  const handleUpdateTown = async (updatedTown: Town) => {
    try {
      const response = await townApi.update(updatedTown.id, updatedTown);
      if (response.success) {
        setTowns((prev) =>
          prev.map((t) => (t.id === updatedTown.id ? updatedTown : t)),
        );
        success(
          "Town Updated",
          `${updatedTown.name} has been updated successfully.`,
        );
      } else {
        error(
          "Failed to Update Town",
          response.error || "An error occurred while updating the town.",
        );
      }
    } catch (err) {
      console.error("Failed to update town:", err);
      error(
        "Failed to Update Town",
        "Network error occurred while updating the town.",
      );
    }
  };

  const handleSaveExpense = async (
    expenseData: Omit<Expense, "id" | "createdAt">,
  ) => {
    try {
      const response = await expenseApi.create(expenseData);
      if (response.success && response.data) {
        setExpenses((prev) => [...prev, response.data!]);
        success(
          "Expense Added",
          `${expenseData.category} expense of GHS ${expenseData.amount.toFixed(2)} has been recorded successfully.`,
        );
      } else {
        error(
          "Failed to Add Expense",
          response.error || "An error occurred while adding the expense.",
        );
      }
    } catch (err) {
      console.error("Failed to add expense:", err);
      error(
        "Failed to Add Expense",
        "Network error occurred while adding the expense.",
      );
    }
  };

  // Town management handlers
  const handleViewTown = (town: Town) => {
    setSelectedTown(town);
    setTownDetailModalOpen(true);
  };

  const handleEditTown = (town: Town) => {
    setSelectedTown(town);
    setEditTownModalOpen(true);
  };

  const handleDeleteTown = async (town: Town) => {
    const confirmed = await confirm({
      title: "Delete Town",
      message: `Are you sure you want to delete ${town.name}? This action cannot be undone.`,
      confirmText: "Delete",
      type: "danger",
    });

    if (confirmed) {
      try {
        const response = await townApi.delete(town.id);
        if (response.success) {
          setTowns((prev) => prev.filter((t) => t.id !== town.id));
          setMembers((prev) => prev.filter((m) => m.townId !== town.id));
          setContributions((prev) => prev.filter((c) => c.townId !== town.id));
          setEvents((prev) => prev.filter((e) => e.townId !== town.id));
          setExpenses((prev) => prev.filter((e) => e.townId !== town.id));
          setBenefits((prev) => prev.filter((b) => b.townId !== town.id));
          success(
            "Town Deleted",
            `${town.name} has been deleted successfully.`,
          );
        } else {
          error("Delete Failed", response.error || "Failed to delete town.");
        }
      } catch (err) {
        console.error("Failed to delete town:", err);
        error("Delete Failed", "An error occurred while deleting the town.");
      }
    }
  };

  const renderContent = () => {
    const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
      <React.Suspense
        fallback={
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="loading-spinner mx-auto mb-4"></div>
              <p className="text-gray-500">Loading...</p>
            </div>
          </div>
        }
      >
        {children}
      </React.Suspense>
    );

    switch (activeTab) {
      case "members":
        return (
          <LazyWrapper>
            <MemberList
              members={members}
              towns={towns}
              selectedTownId={selectedTownId}
              onExport={handleExportMembers}
              onViewMember={handleViewMember}
              onEditMember={handleEditMember}
              onDeleteMember={handleDeleteMember}
              onAddMember={(memberData) => {
                // Convert Partial<Member> to Omit<Member, "id" | "createdAt">
                const memberToCreate: Omit<Member, "id" | "createdAt"> = {
                  username: memberData.username || "",
                  name: memberData.name || "",
                  phone: memberData.phone || "",
                  gender: memberData.gender || "Male",
                  ageGroup: memberData.ageGroup || "Adult",
                  townId: memberData.townId || selectedTownId,
                  registrationDate:
                    memberData.registrationDate ||
                    new Date().toISOString().split("T")[0],
                  totalContributions: memberData.totalContributions || 0,
                  lastPaymentDate: memberData.lastPaymentDate,
                };
                handleAddMember(memberToCreate);
              }}
              onMembersChange={setMembers}
              onRefresh={handleRefreshMembers}
              isRefreshing={isRefreshingMembers}
            />
          </LazyWrapper>
        );
      case "assignments":
        return (
          <RoleBasedRoute requiredPermissions={[PERMISSIONS.VIEW_FINANCES]}>
            <EnhancedMonthlyAssignmentDashboard />
          </RoleBasedRoute>
        );
      case "towns":
        return (
          <LazyWrapper>
            <TownList
              towns={towns}
              contributions={contributions}
              expenses={expenses}
              users={users}
              onAddTown={() => setAddTownModalOpen(true)}
              onEditTown={handleEditTown}
              onViewTown={handleViewTown}
              onDeleteTown={handleDeleteTown}
            />
          </LazyWrapper>
        );
      case "contributions":
        return (
          <ContributionList
            contributions={contributions}
            members={members}
            towns={towns}
            selectedTownId={selectedTownId}
            onExport={handleExportFinancial}
            onAddContribution={handleAddContribution}
            onContributionsChange={setContributions}
            onRefreshData={fetchAllData}
          />
        );
      case "expenses":
        return (
          <ExpenseList
            expenses={expenses}
            towns={towns}
            selectedTownId={selectedTownId}
            onExport={() =>
              exportExpenseReport(expenses, towns, selectedTownId)
            }
            onAddExpense={handleAddExpense}
            onExpensesChange={setExpenses}
          />
        );
      case "benefits":
        return (
          <BenefitList
            members={members}
            towns={towns}
            selectedTownId={selectedTownId}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onBenefitsChange={(newBenefits: any[]) =>
              setBenefits(newBenefits.map(normalizeBenefit))
            }
          />
        );
      case "events":
        return (
          <EventList
            events={events}
            towns={towns}
            members={members}
            selectedTownId={selectedTownId}
            onExport={() => exportEventReport(events, towns, selectedTownId)}
            onViewEvent={handleViewEvent}
            onAddEvent={handleAddEvent}
            onEventsChange={setEvents}
            onRefresh={handleRefreshEvents}
            isRefreshing={isRefreshingEvents}
          />
        );
      case "member-reports":
      case "financial-reports":
      case "expense-reports":
      case "event-reports":
        return (
          <ReportCenter
            members={members}
            contributions={contributions}
            events={events}
            expenses={expenses}
            towns={towns}
            selectedTownId={selectedTownId}
            activeReportType={activeTab}
            onRefresh={handleRefreshReports}
            isRefreshing={isRefreshingReports}
          />
        );
      case "sms":
        return (
          <SMSCenter
            members={members}
            towns={towns}
            selectedTownId={selectedTownId}
            events={events}
          />
        );
      case "email":
        return (
          <LazyWrapper>
            <EmailCenter
              members={members}
              towns={towns}
              selectedTownId={selectedTownId}
            />
          </LazyWrapper>
        );
      case "messages":
        return (
          <LazyWrapper>
            <MemberToastProvider>
              <MessagingPage />
            </MemberToastProvider>
          </LazyWrapper>
        );
      case "audit":
        return (
          <RoleBasedRoute
            requiredPermissions={[
              PERMISSIONS.VIEW_AUDIT_LOGS,
              PERMISSIONS.MANAGE_USERS,
            ]}
          >
            <AuditViewer />
          </RoleBasedRoute>
        );
      case "approvals":
        return (
          <RoleBasedRoute
            requiredPermissions={[
              PERMISSIONS.APPROVE_FINANCES,
              PERMISSIONS.MANAGE_CONTRIBUTIONS,
            ]}
          >
            <FinancialApprovalQueue />
          </RoleBasedRoute>
        );
      case "payment-settings":
        return (
          <RoleBasedRoute
            requiredPermissions={[
              PERMISSIONS.APPROVE_FINANCES,
              PERMISSIONS.MANAGE_USERS,
            ]}
          >
            <PaymentSettings userPermissions={user?.permissions || []} />
          </RoleBasedRoute>
        );
      case "settings":
        return (
          <RoleBasedRoute requiredPermissions={[PERMISSIONS.MANAGE_USERS]}>
            <Settings />
          </RoleBasedRoute>
        );
      case "brand-settings":
        return (
          <RoleBasedRoute requiredPermissions={[PERMISSIONS.MANAGE_USERS]}>
            <BrandSettings />
          </RoleBasedRoute>
        );
      case "stats":
        return (
          <LazyWrapper>
            <StatsDashboard />
          </LazyWrapper>
        );
      case "reminders":
        return (
          <RoleBasedRoute requiredPermissions={[PERMISSIONS.MANAGE_USERS]}>
            <ReminderManager />
          </RoleBasedRoute>
        );
      default:
        return (
          <LazyWrapper>
            <Dashboard
              stats={dashboardStats}
              selectedTownId={selectedTownId}
              towns={towns}
              members={members}
              contributions={contributions}
              events={events}
              onQuickAction={handleQuickAction}
              onRefresh={handleRefreshDashboard}
              isRefreshing={isRefreshingDashboard}
            />
          </LazyWrapper>
        );
    }
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case "members":
        return "Family Members";
      case "towns":
        return "Family Management";
      case "contributions":
        return "Contributions";
      case "assignments":
        return "Monthly Assignments";
      case "expenses":
        return "Expenses";
      case "benefits":
        return "Benefits";
      case "events":
        return "Events";
      case "sms":
        return "SMS Center";
      case "email":
        return "Email Center";
      case "messages":
        return "Messages";
      case "audit":
        return "Audit Trail";
      case "approvals":
        return "Financial Approvals";
      case "payment-settings":
        return "Payment Settings";
      case "settings":
        return "Settings";
      case "brand-settings":
        return "Brand Settings";
      case "reminders":
        return "Automatic Reminders";
      default:
        return "Dashboard";
    }
  };

  const getPageDescription = () => {
    switch (activeTab) {
      case "members":
        return "Manage family members, view profiles, and track membership information.";
      case "towns":
        return "Manage family branches across different towns and locations.";
      case "contributions":
        return "Track and manage family member contributions and payments.";
      case "assignments":
        return "Manage monthly assignments and track member participation.";
      case "expenses":
        return "Monitor and approve family organization expenses.";
      case "benefits":
        return "Manage family member benefits and welfare programs.";
      case "events":
        return "Organize and track family events and gatherings.";
      case "sms":
        return "Send bulk SMS messages to family members.";
      case "email":
        return "Send bulk emails to family members with templates and tracking.";
      case "messages":
        return "Send and receive direct messages with family members and administrators.";
      case "reminders":
        return "Configure and manage automatic SMS reminders for payments, events, and birthdays.";
      default:
        return "Family membership management dashboard with comprehensive tools for administration.";
    }
  };

  return (
    <>
      <SEOHead
        title={getPageTitle()}
        description={getPageDescription()}
        canonical={window.location.href}
        noIndex={activeTab === "settings" || activeTab === "brand-settings"}
      />
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary">
        <Sidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <div
          className={`flex flex-col min-h-screen transition-all duration-300 ${
            sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"
          }`}
        >
          <Header
            towns={towns}
            selectedTownId={selectedTownId}
            onTownChange={setSelectedTownId}
            onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
            onSwitchToMemberPortal={handleSwitchToMemberPortal}
            onNavigateToMessages={handleNavigateToMessages}
          />

          <main className="flex-1 p-4 md:p-6">
            <React.Suspense
              fallback={
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="loading-spinner mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading...</p>
                  </div>
                </div>
              }
            >
              {renderContent()}
            </React.Suspense>
          </main>
        </div>

        {/* Modals */}
        <MemberDetailModal
          isOpen={memberDetailModalOpen}
          onClose={() => {
            setMemberDetailModalOpen(false);
            setSelectedMember(null);
          }}
          member={selectedMember}
          town={
            selectedMember
              ? towns.find((t) => t.id === selectedMember.townId) || null
              : null
          }
          contributions={contributions}
          onEdit={(member) => {
            setMemberDetailModalOpen(false);
            handleEditMember(member);
            handleEditMember(member);
          }}
          onSimulate={handleSimulateMember}
        />

        <EditMemberModal
          isOpen={editMemberModalOpen}
          onClose={() => {
            setEditMemberModalOpen(false);
            setSelectedMember(null);
          }}
          member={selectedMember}
          towns={towns}
          onSave={handleSaveMember}
        />

        <EventAttendanceModal
          isOpen={attendanceModalOpen}
          onClose={() => {
            setAttendanceModalOpen(false);
            setSelectedEvent(null);
          }}
          event={selectedEvent}
          members={members}
          towns={towns}
          onSaveAttendance={handleSaveAttendance}
        />

        <MoMoPaymentModal
          isOpen={momoPaymentModalOpen}
          onClose={() => {
            setMomoPaymentModalOpen(false);
            setPaymentData(null);
          }}
          amount={paymentData?.amount || 0}
          contributionType={paymentData?.type || ""}
          memberName={paymentData?.memberName || ""}
          onPaymentComplete={async (reference) => {
            console.log("Payment completed with reference:", reference);

            // Create contribution record
            if (paymentData) {
              await handleAddContribution({
                memberId: paymentData.memberId,
                townId: paymentData.townId,
                amount: paymentData.amount,
                type: paymentData.type,
                paymentMethod: "MoMo",
                date: new Date().toISOString().split("T")[0],
                status: "Completed",
                reference,
              });
            }

            setMomoPaymentModalOpen(false);
            setPaymentData(null);
          }}
        />

        <AddTownModal
          isOpen={addTownModalOpen}
          onClose={() => setAddTownModalOpen(false)}
          onSave={handleSaveTown}
        />

        <EditTownModal
          isOpen={editTownModalOpen}
          onClose={() => {
            setEditTownModalOpen(false);
            setSelectedTown(null);
          }}
          town={selectedTown}
          onSave={handleUpdateTown}
        />

        <TownDetailModal
          isOpen={townDetailModalOpen}
          onClose={() => {
            setTownDetailModalOpen(false);
            setSelectedTown(null);
          }}
          town={selectedTown}
          towns={towns}
          users={users}
          contributions={contributions}
          expenses={expenses}
          members={members}
          onRefreshData={fetchAllData}
        />

        <AddExpenseModal
          isOpen={addExpenseModalOpen}
          onClose={() => setAddExpenseModalOpen(false)}
          onSave={handleSaveExpense}
          towns={towns}
          selectedTownId={selectedTownId}
        />

        <ToastContainer toasts={toasts} onRemove={removeToast} />

        <ConfirmationModal
          isOpen={isOpen}
          title={options.title}
          message={options.message}
          confirmText={options.confirmText}
          cancelText={options.cancelText}
          type={options.type}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />

        {/* Help System Components */}
        <HelpModal />
        <NewGuideNotifications />
      </div>
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrandProvider>
        <PWAProvider>
          <AuthProvider>
            <ToastProvider>
              <HelpProvider>
                <Router>
                  <AppContent />
                  {/* PWA Components */}
                  <PWAInstallPrompt />
                  <MobileNavigation
                    onQuickAction={(action) => {
                      // Handle quick actions from mobile navigation
                      // We need to pass this through to the AppContent component
                      // For now, we'll use a global event or context to communicate
                      const event = new CustomEvent("mobile-quick-action", {
                        detail: { action },
                      });
                      window.dispatchEvent(event);
                    }}
                  />
                </Router>
              </HelpProvider>
            </ToastProvider>
          </AuthProvider>
        </PWAProvider>
      </BrandProvider>
    </ErrorBoundary>
  );
}

export default App;
